import { createContext, useContext, useState, useCallback, useMemo } from 'react';
import { clearRequestCache } from '../services/requestCache.js';

const AuthContext = createContext(null);

const ADMIN_ROLES = ['administrator'];

/**
 * Provides authenticated user info to the entire component tree.
 * The value is set by AuthGuard after a successful /api/user/info call.
 */
export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);

  const login = useCallback((userData) => {
    // Flush cached responses so newly-authenticated requests are always fresh.
    clearRequestCache();
    setUser(userData);
  }, []);

  const logout = useCallback(() => {
    // Flush before navigating to avoid serving stale authenticated content.
    clearRequestCache();
    setUser(null);
    window.location.href = '/user/logout';
  }, []);

  const isAdmin = useMemo(() => {
    if (!user?.roles || !Array.isArray(user.roles)) return false;
    return user.roles.some((r) => ADMIN_ROLES.includes(r));
  }, [user]);

  // Permissions granted to this account, straight from /api/user/info. A gate
  // keyed on one of these opens to any role that holds it, where `isAdmin`
  // only ever opens to the global 'administrator' role.
  const hasPermission = useCallback(
    (permission) => Array.isArray(user?.permissions) && user.permissions.includes(permission),
    [user],
  );

  return (
    <AuthContext.Provider value={{ user, login, logout, isAdmin, hasPermission }}>
      {children}
    </AuthContext.Provider>
  );
}

/**
 * Returns { user, login, logout, isAdmin, hasPermission } where user is
 * { uid, name, timezone, roles, permissions } once authenticated, or null
 * during loading.
 */
export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
}

/**
 * Même valeur, mais `null` hors AuthProvider au lieu de lever.
 *
 * POURQUOI : le rendu PUBLIC d'une page builder (`BuilderPublicRenderer`, monté
 * dans `AppRouter` AVANT `OrbitShell`, et la racine SSG) n'a délibérément aucun
 * provider d'auth — il n'y a pas de session à lire. Un composant partagé entre
 * les deux mondes (le canevas et ses blocs) ne peut donc pas appeler `useAuth`,
 * qui vide l'écran public. Il lit ici, et retombe sur le comportement anonyme
 * (aucune permission) quand personne n'est authentifié.
 */
export function useOptionalAuth() {
  return useContext(AuthContext);
}
